import { flexRender, type Table as TanstackTable } from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ErrorState } from '@/shared/components/error-state';
import { cn } from '@/shared/lib/utils';

import { DataTableSkeleton } from './data-table-skeleton';

interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  isLoading?: boolean;
  isFetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Rendered when the query succeeded but returned nothing. */
  emptyState?: React.ReactNode;
  /** Makes rows clickable. Prefer a link in a cell where possible. */
  onRowClick?: (row: TData) => void;
  className?: string;
}

/**
 * Renders a TanStack table with shadcn's table primitives.
 *
 * Composition, not configuration: this component draws rows and nothing else.
 * Pagination, toolbars and column visibility are separate components the page
 * assembles as it needs them. There is no `<DataTable filters={...} actions={
 * ...} onEdit={...}>` god-prop — that road ends in a universal table engine
 * nobody can modify safely (§40).
 */
export function DataTable<TData>({
  table,
  isLoading = false,
  isFetching = false,
  error,
  onRetry,
  emptyState,
  onRowClick,
  className,
}: DataTableProps<TData>) {
  const columnCount = table.getAllLeafColumns().length;
  const rows = table.getRowModel().rows;

  return (
    <div className={cn('bg-card relative overflow-hidden rounded-lg border', className)}>
      {/* A background refetch shows a hairline progress bar rather than
          replacing the table with a skeleton — the old data stays readable
          and the layout does not jump (§64). */}
      {isFetching && !isLoading && (
        <div
          className="bg-primary/70 absolute inset-x-0 top-0 h-0.5 animate-pulse"
          role="status"
          aria-label="Refreshing"
        />
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={header.getSize() !== 150 ? { width: header.getSize() } : undefined}
                    className="h-9 text-xs font-semibold"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <DataTableSkeleton
                columnCount={columnCount}
                rowCount={table.getState().pagination.pageSize}
              />
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount} className="p-0">
                  <ErrorState error={error} onRetry={onRetry} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount} className="p-0">
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn(
                    'data-[state=selected]:bg-accent/60',
                    onRowClick && 'cursor-pointer',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2 text-xs">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
